import React, { useEffect, useState, useMemo } from 'react';
import { UserCog, Plus, Eye, Edit, Trash2, Mail, Phone, Star, MoreVertical, Send } from 'lucide-react';
import InstructorCard from './InstructorCard';
import {
  PageHeader, AppButton, ViewToggle, StatusBadge, UserAvatar, EmptyState,
  AppCard, AppCardContent, ConfirmModal, SearchInput,
  FilterButton, ActiveFilterChips, FilterPanel, FilterSelectField,
  useListFilters,
} from '../ui';
import apiClient from '../../utils/apiClient';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../../contexts/BranchContext';
import './Instructors.scss';
import CommonToaster from '../Common/CommonToaster';
import OverLimitBanner from '../Common/OverLimitBanner';
import { usePlanUsage, isOverLimit, isStrictlyOverLimit } from '../../hooks/usePlanUsage';
import { hasPermission } from '../../utils/permissions';
import { getCurrentUser } from '../../utils/auth';

const InstructorsPage = () => {
  const [instructors, setInstructors] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [view, setView] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();
  const canManageInstructors = hasPermission('add_edit_instructors');
  // Removing an instructor is the owner's alone on the server.
  const canDeleteInstructors = getCurrentUser()?.usertype === 'superadmin';

  // Migrated off the deprecated FilterModal onto the FilterPanel pattern (D3),
  // 2026-08-24. The page previously ran TWO filter UIs side by side — an inline
  // `<select>` shown `d-none d-md-block` and a separate button + modal shown
  // `d-md-none` — which is the thing D3 exists to end: one drawer at every width, so
  // a fix or a new filter lands once instead of in two places that can drift.
  //
  // ⚠️ `empty: ''`, not the hook's "all" default: this page's option list leads with
  // `<option value="">All Status</option>` and the predicate tests falsiness.
  //
  // Named `listFilters` because `filters` is already this page's search/status state.
  const listFilters = useListFilters({
    status: {
      label: 'Status',
      empty: '',
      options: [
        { value: '', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
    },
  });

  // One direction only: the drawer owns the value, the page's existing `filters`
  // object mirrors it so every downstream reader (filteredInstructors, the export)
  // keeps working untouched.
  useEffect(() => {
    setFilters((f) => (f.status === listFilters.values.status
      ? f
      : { ...f, status: listFilters.values.status }));
  }, [listFilters.values.status]);

  // Plan usage limits
  const { usage } = usePlanUsage();
  const instructorLimit = isOverLimit(usage?.instructors);
  const instructorStrictly = isStrictlyOverLimit(usage?.instructors);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const { data } = await apiClient.get('/instructors/list', { params: filters });
        setInstructors(data.instructors || []);
      } catch {
        setToast({ show: true, message: 'Failed to load instructors.', type: 'error' });
      }
      setLoading(false);
    }
    fetchData();
  }, [filters, selectedBranchId]);

  const filteredInstructors = useMemo(() => {
    let list = instructors;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(i =>
        i.name?.toLowerCase().includes(q) ||
        i.email?.toLowerCase().includes(q) ||
        i.subject?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [instructors, filters.search]);

  const [confirmAction, setConfirmAction] = useState(null);

  const handleDelete = (inst) => {
    setConfirmAction({
      title: 'Delete Instructor',
      message: `Are you sure you want to delete instructor "${inst.name}"?`,
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await apiClient.delete(`/instructors/${inst._id}`);
          setInstructors(prev => prev.filter(i => i._id !== inst._id));
          setToast({ show: true, message: 'Instructor deleted.', type: 'success' });
        } catch {
          setToast({ show: true, message: 'Failed to delete instructor.', type: 'error' });
        }
      },
    });
  };

  const resendWelcomeEmail = (inst) => {
    setConfirmAction({
      title: 'Resend Welcome Email',
      message: `Are you sure you want to resend welcome email to "${inst.name}"?`,
      variant: 'info',
      confirmText: 'Resend',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await apiClient.post(`/instructors/${inst._id}/resend-welcome-email`);
          setToast({ show: true, message: 'Welcome email resent successfully.', type: 'success' });
        } catch {
          setToast({ show: true, message: 'Failed to resend welcome email.', type: 'error' });
        }
      },
    });
  };

  return (
    <div className="instructors-page">
      <OverLimitBanner
        resourceName="Instructors"
        used={instructorLimit.used}
        max={instructorLimit.max}
        overLimit={instructorLimit.overLimit}
        strictlyOver={instructorStrictly.overLimit}
      />
      <PageHeader
        title="Instructors"
        description={`Manage ${instructors.length} instructors at your institute`}
        icon={UserCog}
      />

      {/* Toolbar: Filters (left) — View toggle + Actions (right) */}
      <div className="inst-toolbar">
        <div className="inst-toolbar-left">
          <div className="inst-search">
            {/* Was a hand-rolled input with its own icon, border and focus ring. The
                ring read `var(--brand-primary, #2563eb)` — a token declared NOWHERE,
                so it painted the v1 blue in both themes and could never follow the
                institute's brand. `SearchInput` is the library's control and is
                already themed. `.inst-search` survives as a layout-only wrapper. */}
            <SearchInput
              value={filters.search}
              onChange={(value) => setFilters(f => ({ ...f, search: value }))}
              placeholder="Search instructors..."
            />
          </div>

          <FilterButton count={listFilters.count} onClick={listFilters.open} />
        </div>

        <div className="inst-toolbar-right">
          <ViewToggle view={view} onViewChange={setView} />
          {canManageInstructors && (
            <AppButton className="inst-add-btn" size="sm" onClick={() => navigate('/instructors/add')} disabled={instructorLimit.overLimit} title={instructorLimit.overLimit ? `Plan limit reached (${instructorLimit.used}/${instructorLimit.max})` : ''}>
              <Plus size={16} />
              <span className="inst-add-btn-text">Add Instructor</span>
            </AppButton>
          )}
        </div>
      </div>

      <ActiveFilterChips items={listFilters.activeFilters} onClearAll={listFilters.reset} />

      <FilterPanel {...listFilters.panelProps} subtitle="Narrow the instructor list.">
        <FilterSelectField {...listFilters.selectProps('status')} />
      </FilterPanel>

      {/* Content */}
      {loading ? (
        <div className="inst-loading">
          <div className="spinner-border spinner-border-sm" role="status" />
          <span>Loading instructors...</span>
        </div>
      ) : filteredInstructors.length === 0 ? (
        <EmptyState
          icon={<UserCog size={40} />}
          title="No instructors found"
          description="Try adjusting your search criteria or add a new instructor"
        />
      ) : view === 'grid' ? (
        <div className="inst-grid">
          {filteredInstructors.map(instructor => (
            <InstructorCard
              key={instructor._id}
              instructor={instructor}
              onView={inst => navigate(`/instructors/${inst._id}`)}
              onEdit={inst => navigate(`/instructors/edit/${inst._id}`)}
              onDelete={canDeleteInstructors ? handleDelete : undefined}
              resendWelcomeEmail={resendWelcomeEmail}
              canManage={canManageInstructors}
            />
          ))}
        </div>
      ) : (
        <AppCard className="inst-table-card">
          <AppCardContent className="p-0">
            <div className="inst-table-wrapper">
              <table className="inst-table">
                <thead>
                  <tr>
                    <th>Instructor</th>
                    <th>Specialization</th>
                    <th>Experience</th>
                    <th className="text-center">Students</th>
                    <th className="text-center">Rating</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInstructors.map(instructor => {
                    const courses = instructor.subject ? instructor.subject.split(',').map(c => c.trim()) : [];
                    return (
                      <tr key={instructor._id}>
                        <td>
                          <div className="inst-cell-user">
                            <UserAvatar name={instructor.name} size="sm" />
                            <div>
                              <span
                                className="inst-cell-user__name"
                                onClick={() => navigate(`/instructors/${instructor._id}`)}
                              >
                                {instructor.name}
                              </span>
                              <span className="inst-cell-user__email">{instructor.email}</span>
                            </div>
                          </div>
                        </td>
                        <td>{courses.join(', ') || '-'}</td>
                        <td>{instructor.experience ? `${instructor.experience} yrs` : '-'}</td>
                        <td className="text-center">{instructor.students || '-'}</td>
                        <td className="text-center">
                          <span className="inst-rating">
                            <Star size={14} fill="currentColor" />
                            {instructor.rating || '—'}
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={instructor.status?.toLowerCase() || 'active'}>
                            {instructor.status || 'Active'}
                          </StatusBadge>
                        </td>
                        <td className="text-end inst-cell-actions">
                          <button className="inst-action-btn" title="View" onClick={() => navigate(`/instructors/${instructor._id}`)}>
                            <Eye size={15} />
                          </button>
                          {canManageInstructors && (
                            <>
                              <button className="inst-action-btn" title="Edit" onClick={() => navigate(`/instructors/edit/${instructor._id}`)}>
                                <Edit size={15} />
                              </button>
                              <button className="inst-action-btn" title="Resend Email" onClick={() => resendWelcomeEmail(instructor)}>
                                <Send size={15} />
                              </button>
                              {canDeleteInstructors && (
                                <button className="inst-action-btn inst-action-btn--danger" title="Delete" onClick={() => handleDelete(instructor)}>
                                  <Trash2 size={15} />
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </AppCardContent>
        </AppCard>
      )}

      <CommonToaster
        show={toast?.show}
        message={toast?.message}
        type={toast?.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!confirmAction}
        onHide={() => setConfirmAction(null)}
        onConfirm={() => confirmAction?.onConfirm()}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText={confirmAction?.confirmText || 'Confirm'}
        variant={confirmAction?.variant || 'danger'}
      />
    </div>
  );
};

export default InstructorsPage;
